import { createContext, useContext, useState, useEffect } from 'react';
import translations from './translations.json';

const LanguageContext = createContext(null);

export const SUPPORTED_LANGUAGES = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' }
];

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('telcavoip_language');
    if (saved && translations[saved]) return saved;
    
    // Check browser language
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && translations[browserLang]) return browserLang;
    
    // Default to Italian
    return 'it';
  });

  const setLanguage = (lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
      localStorage.setItem('telcavoip_language', lang);
      document.documentElement.lang = lang;
    }
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Translation function with nested path support
  const t = (path, fallback = '') => {
    const keys = path.split('.');
    let result = translations[language];
    
    for (const key of keys) {
      if (result && typeof result === 'object' && key in result) {
        result = result[key];
      } else {
        // Try fallback to Italian, then English
        result = translations['it'];
        for (const k of keys) {
          if (result && typeof result === 'object' && k in result) {
            result = result[k];
          } else {
            result = translations['en'];
            for (const k2 of keys) {
              if (result && typeof result === 'object' && k2 in result) {
                result = result[k2];
              } else {
                return fallback || path;
              }
            }
            break;
          }
        }
        break;
      }
    }
    
    return result || fallback || path;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, SUPPORTED_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
